import classNames from 'classnames'
import { map, sortBy } from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { Row, Col, Collapse } from 'react-bootstrap'
import Tappable from 'react-tappable'
import FlipMove from 'react-flip-move'

import Link from 'components/link'
import { GOAL_STATES, GOALS, GOALS_EVENT_CTA } from 'registry/constants'
import * as consts from 'registry/constants'
import { DEVICE_PLATFORMS } from 'shared/constants'
import { track } from 'lib/analytics'
import withCurrentUser from 'shared/HOC/withCurrentUser'

import { registryExternalPath } from 'lib/urls'
import SetupGoal, {
  ArrivalDate,
  BabylistStoreItems,
  CashFund,
  EventDetails,
  FamilyDetails,
  HelloBabyBox,
  ImportRegistry,
  InsertCards,
  OtherStoreItems,
  PersonalizeRegistry,
  RegistryDiscount,
  ShippingAddress,
  FortyPercentChecklist,
} from '../setup-goal'
import css from './setup-goals.scss'

const TABLET_BREAKPOINT = 991
const DESKTOP_BREAKPOINT = 1229
const SLIDES_TO_SHOW_DESKTOP = 4

const GOAL_COMPONENTS = {
  [GOALS.arrivalDate]: ArrivalDate,
  [GOALS.babylistStoreItems]: BabylistStoreItems,
  [GOALS.cashFund]: CashFund,
  [GOALS.eventDetails]: EventDetails,
  [GOALS.externalRegistry]: ImportRegistry,
  [GOALS.familyDetails]: FamilyDetails,
  [GOALS.helloBabyBox]: HelloBabyBox,
  [GOALS.insertCards]: InsertCards,
  [GOALS.otherStoreItems]: OtherStoreItems,
  [GOALS.personalizeRegistry]: PersonalizeRegistry,
  [GOALS.registryDiscount]: RegistryDiscount,
  [GOALS.setupGoal]: SetupGoal,
  [GOALS.shippingAddress]: ShippingAddress,
  [GOALS.checklistFortyPercentComplete]: FortyPercentChecklist,
}

class SetupGoals extends Component {
  static propTypes = {
    currentUser: PropTypes.object,
    devicePlatform: PropTypes.string,
    expanded: PropTypes.bool,
    goals: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        state: PropTypes.string,
      })
    ),
    hasSeenHelloBabyBoxQualified: PropTypes.bool.isRequired,
    onToggleExpandedState: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func,
  }

  static contextTypes = {
    showExtensionInstall: PropTypes.bool.isRequired,
  }

  constructor(props) {
    super(props)

    this.state = {
      // We always server render 4 tiles. On tablet you see 4 tiles at first and
      // then it switches to 3 tiles when page loads. The 4 tiles are squished and
      // ugly. Solution is to hide the text on all tiles when server rendering until
      // the component mounts and we've switched to 3 tiles.
      hideText: true,
      slidesToShow: SLIDES_TO_SHOW_DESKTOP,
    }
    this.handleResizeWindow = this.handleResizeWindow.bind(this)
  }

  componentDidMount() {
    this.handleResizeWindow()
    this.setState({ hideText: false })
    window.addEventListener('resize', this.handleResizeWindow)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResizeWindow)
  }

  handleResizeWindow() {
    let slidesToShow

    if (window.innerWidth < TABLET_BREAKPOINT) {
      slidesToShow = 2
    } else if (window.innerWidth > DESKTOP_BREAKPOINT) {
      slidesToShow = 4
    } else {
      slidesToShow = 3
    }
    if (this.state.slidesToShow != slidesToShow) {
      this.setState({ slidesToShow })
    }
  }

  handleClick(id) {
    return () => {
      this.trackEvent(id)
      this.actionForGoal(id)()
    }
  }

  getGoalComponents(start = 0, end = 999) {
    return map(
      this.sortedGoals().slice(start, end),
      ({ id, ...props }, idx) => {
        const GoalComponent = GOAL_COMPONENTS[id]
        return (
          <Col sm={6} md={4} lg={3} key={id} className={css.goalContainer}>
            <GoalComponent
              {...props}
              alternateIcon={(start + idx) % 2 == 0}
              hideText={this.state.hideText}
              id={id}
              onClick={this.handleClick(id)}
            />
          </Col>
        )
      }
    )
  }

  actionForGoal(goal) {
    const { devicePlatform, setActiveModal } = this.props
    switch (goal) {
      case GOALS.arrivalDate:
        return setActiveModal.bind(null, consts.ADD_ARRIVAL_DATE)
      case GOALS.babylistStoreItems:
        return setActiveModal.bind(null, consts.ADD_FROM_BABYLIST_STORE)
      case GOALS.eventDetails:
        return setActiveModal.bind(null, consts.ADD_SHOWER_INFO)
      case GOALS.externalRegistry:
        return () => window.open(registryExternalPath)
      case GOALS.familyDetails:
        return setActiveModal.bind(null, consts.ADD_FAMILY_DETAILS)
      case GOALS.helloBabyBox:
        return setActiveModal.bind(null, consts.HELLO_BABY_BOX)
      case GOALS.cashFund:
        return setActiveModal.bind(null, consts.ADD_CASH_FUND)
      case GOALS.insertCards:
        return setActiveModal.bind(null, consts.ORDER_INSERT_CARDS)
      case GOALS.otherStoreItems:
        if (this.context.showExtensionInstall) {
          return setActiveModal.bind(null, consts.INSTALL_BABYLIST_EXTENSION)
        } else if (devicePlatform != DEVICE_PLATFORMS.desktop) {
          return setActiveModal.bind(null, consts.INSTALL_BABYLIST_APP)
        } else {
          return setActiveModal.bind(null, consts.ADD_BABYLIST_BUTTON)
        }
      case GOALS.personalizeRegistry:
        return setActiveModal.bind(null, consts.PERSONALIZE_YOUR_REGISTRY)
      case GOALS.registryDiscount:
        return setActiveModal.bind(null, consts.REGISTRY_DISCOUNT)
      case GOALS.shippingAddress:
        return setActiveModal.bind(null, consts.ADD_SHIPPING_ADDRESS)

      default:
        return () => {}
    }
  }

  sortedGoals() {
    return sortBy(this.props.goals, (goal, idx) =>
      goal.id === GOALS.helloBabyBox
        ? this.props.hasSeenHelloBabyBoxQualified
          ? 3
          : 0
        : goal.state === GOAL_STATES.INCOMPLETE
          ? 0
          : 1
    )
  }

  trackEvent(goal) {
    const { currentUser } = this.props

    if (!currentUser || !GOALS_EVENT_CTA[goal]) {
      return
    }

    track.registrySetupStepClicked({
      eventCta: GOALS_EVENT_CTA[goal],
      registryId: currentUser.currentRegistry?.id,
      registryOwnerId: currentUser.currentRegistry?.ownerId,
      isRegistryOwner: currentUser.currentRegistry?.ownerId === currentUser.id,
    })
  }

  render() {
    const { expanded, goals, onToggleExpandedState } = this.props
    const { slidesToShow } = this.state
    return (
      <div
        className={classNames(css.container, 'clearfix', 'registry-setup-b')}
      >
        <Row className={classNames(css.goalsRow)}>
          <FlipMove duration={400} leaveAnimation="none">
            {this.getGoalComponents(0, slidesToShow)}
          </FlipMove>
        </Row>
        <Collapse in={expanded}>
          <Row className={css.goalsRow}>
            {this.getGoalComponents(slidesToShow)}
          </Row>
        </Collapse>
        <div className="mtl text-center visible-xs">
          <Tappable
            className="pvl phl h7 text-uppercase"
            component={Link}
            onClick={onToggleExpandedState}
          >
            {expanded ? (
              <span>
                See less <i className="fa fa-angle-up" />
              </span>
            ) : (
              <span>
                See all <i className="fa fa-angle-down" />
              </span>
            )}
          </Tappable>
        </div>
      </div>
    )
  }
}

export default withCurrentUser(SetupGoals)
